// src/platform/OperatingAoisPanel.jsx
import {
    useEffect,
    useState,
    useImperativeHandle,
    forwardRef,
} from "react";
import { Loader2 } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";
import PlatformOperatingAoiMap from "./components/PlatformOperatingAoiMap";
import { usePlatformOrgContext } from "../stores/usePlatformOrgContext";
import OperatingAoiCoveragePanel from "./components/OperatingAoiCoveragePanel";
import OperatingAoiAssetSummary from "./components/OperatingAoiAssetSummary";
import { useOperatingAoiCoverage } from "./lib/useOperatingAoiCoverage";
import { useImageryStackStore } from "../stores/useImageryStackStore";

const OperatingAoisPanel = forwardRef(function OperatingAoisPanel(_, ref) {
    const { activeOrgId } = usePlatformOrgContext();

    const [aois, setAois] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    const loadCatalog = useImageryStackStore(s => s.loadCatalog);

    // ----------------------------
    // LOAD LIST
    // ----------------------------
    async function loadAois(selectId = null) {
        if (!activeOrgId) {
            setAois([]);
            setSelectedId(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await fetchWithAuth(
                "/api/v1/platform/operating-aois"
            );
            if (!res.ok) throw new Error("Failed to load operating AOIs");

            const data = await res.json();
            const list = Array.isArray(data.operating_aois)
                ? data.operating_aois
                : [];

            setAois(list);

            setSelectedId(prev => {
                if (prev && list.some(a => a.id === prev)) {
                    return prev;              // preserve valid selection
                }
                return list.length > 0
                    ? list[0].id              // default AOI
                    : null;
            });

        } catch (e) {
            console.error(e);
            setError("Failed to load operating AOIs");
            setAois([]);
            setSelectedId(null);
        } finally {
            setLoading(false);
        }
    }

    // ----------------------------
    // LOAD GEOMETRY (MAP)
    // ----------------------------
    async function loadAoiGeometry(aoiId) {
        const res = await fetchWithAuth(
            `/api/v1/platform/operating-aois/${aoiId}`
        );
        if (!res.ok) {
            throw new Error("Failed to load AOI geometry");
        }
        return await res.json();
    }

    // ----------------------------
    // IMPERATIVE API (upload hook)
    // ----------------------------
    useImperativeHandle(ref, () => ({
        reload: (selectId) => loadAois(selectId),
    }));

    //
    // HANDLE ORG SWITCH
    //
    useEffect(() => {
        if (!activeOrgId) {
            setAois([]);
            setSelectedId(null);
            return;
        }

        loadAois();
    }, [activeOrgId]);

    //
    // HANDLE OPERATING AOI SELECTION
    // Drives the imagery catalog server-side via /imagery/footprints?operating_aoi=...
    //
    useEffect(() => {
        if (selectedId) {
            loadCatalog({ operatingAoiId: selectedId });
        } else {
            loadCatalog();   // global scope
        }
    }, [selectedId, loadCatalog]);

    // ----------------------------
    // RELOAD ON ORG CHANGE
    // ----------------------------
    useEffect(() => {
        loadAois();
    }, [activeOrgId]);

    // ----------------------------
    // EMPTY ORG STATE
    // ----------------------------
    if (!activeOrgId) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Select an organization to view Operating AOIs
            </div>
        );
    }

    // ----------------------------
    // RENDER
    // ----------------------------
    return (
        <div className="flex h-full">
            {/* LEFT COLUMN */}
            <div className="w-80 shrink-0 border-r border-bg-border bg-bg-soft p-4 flex flex-col">
                <h2 className="text-sm font-semibold">Operating AOIs</h2>
                <p className="mt-2 text-xs text-gray-400">
                    Customer operating footprints used for scoping visibility and analysis.
                </p>

                <div className="mt-4 flex-1 overflow-auto">
                    {loading && (
                        <div className="flex justify-center pt-6">
                            <Loader2 className="animate-spin text-gray-400" />
                        </div>
                    )}

                    {error && (
                        <div className="pt-4 text-sm text-red-400">
                            {error}
                        </div>
                    )}

                    {!loading && !error && aois.length === 0 && (
                        <div className="pt-4 text-sm text-fg-secondary">
                            No operating AOIs defined.
                        </div>
                    )}

                    {!loading &&
                        !error &&
                        aois.map(aoi => (
                            <button
                                key={aoi.id}
                                onClick={() => setSelectedId(aoi.id)}
                                className={`w-full text-left px-1 py-1 mb-0 rounded border border-transparent
                  ${selectedId === aoi.id
                                        ? "bg-accent-action/20 border-accent-action/40 text-orange-300"
                                        : "hover:bg-bg text-gray-300"
                                    }`}
                            >
                                <div className="text-[11px] truncate">
                                    {aoi.is_default ? "✓ " : ""}
                                    {aoi.name}
                                </div>

                                <div className="text-[11px] text-fg-secondary mt-1">
                                    Source: {aoi.source}
                                </div>
                            </button>
                        ))}
                </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="flex-1 bg-black flex flex-col">
                <PlatformOperatingAoiMap
                    aois={aois}
                    selectedId={selectedId}
                    loadAoiGeometry={loadAoiGeometry}
                />

                <OperatingAoiCoveragePanel aoiId={selectedId} />

                <OperatingAoiAssetSummary aoiId={selectedId} />
            </div>

        </div>
    );
});

export default OperatingAoisPanel;